// AI Context Enhancer for GrapesJS
// Makes AI aware of all available components, blocks, and tools in the system

export default function aiContextEnhancer(editor, opts = {}) {
  const options = {
    enableComponentAwareness: opts.enableComponentAwareness !== false,
    enableBlockAwareness: opts.enableBlockAwareness !== false,
    enableToolAwareness: opts.enableToolAwareness !== false,
    enableStyleAwareness: opts.enableStyleAwareness !== false,
    enableRealEstateContext: opts.enableRealEstateContext !== false,
    enableCustomPrompts: opts.enableCustomPrompts !== false,
    maxContextLength: opts.maxContextLength || 4000
  };

  // Dynamically extract components from the block manager
  function extractBlockManagerComponents() {
    const blockManager = editor.BlockManager;
    const blocks = blockManager.getAll();
    const components = {};
    
    // Group blocks by category
    blocks.forEach(block => {
      const category = block.get('category') || 'basic';
      const id = block.get('id');
      const label = block.get('label');
      const content = block.get('content');
      const metadata = block.get('metadata'); // Extract enhanced metadata
      
      if (!components[category]) {
        components[category] = {};
      }
      
      // Extract meaningful information from the block
      let description = '';
      let usage = '';
      let tags = [];
      let difficulty = 'beginner';
      let props = {};
      let dependencies = [];
      let responsive = {};
      let accessibility = {};
      let seo = {};
      let interactions = [];
      let examples = [];
      let bestPractices = [];
      
      // If enhanced metadata exists, use it
      if (metadata) {
        description = metadata.description || '';
        tags = metadata.tags || [];
        difficulty = metadata.difficulty || 'beginner';
        props = metadata.props || {};
        dependencies = metadata.dependencies || [];
        responsive = metadata.responsive || {};
        accessibility = metadata.accessibility || {};
        seo = metadata.seo || {};
        interactions = metadata.interactions || [];
        examples = metadata.examples || [];
        bestPractices = metadata.bestPractices || [];
      } else {
        // Fallback to legacy content analysis
        if (content) {
          const contentStr = typeof content === 'string' ? content : JSON.stringify(content);
          
          if (contentStr.includes('hero') || contentStr.includes('search')) {
            description = 'Hero section with search functionality';
            usage = 'Use for main landing pages with property search';
            tags = ['hero', 'search', 'landing'];
          } else if (contentStr.includes('nav') || contentStr.includes('menu')) {
            description = 'Navigation component';
            usage = 'Use for site navigation and menus';
            tags = ['navigation', 'menu', 'header'];
          } else if (contentStr.includes('grid') || contentStr.includes('uk-grid')) {
            description = 'Grid layout component';
            usage = 'Use for creating multi-column layouts';
            tags = ['layout', 'grid', 'columns'];
          } else if (contentStr.includes('card') || contentStr.includes('uk-card')) {
            description = 'Card component';
            usage = 'Use for displaying content in card format';
            tags = ['card', 'content', 'display'];
          } else if (contentStr.includes('button') || contentStr.includes('uk-button')) {
            description = 'Button component';
            usage = 'Use for actions and navigation';
            tags = ['button', 'action', 'interaction'];
          } else if (contentStr.includes('section') || contentStr.includes('uk-section')) {
            description = 'Section container';
            usage = 'Use for major page sections';
            tags = ['section', 'container', 'layout'];
          } else if (contentStr.includes('container') || contentStr.includes('uk-container')) {
            description = 'Container component';
            usage = 'Use to constrain content width';
            tags = ['container', 'layout', 'responsive'];
          } else if (contentStr.includes('form') || contentStr.includes('input')) {
            description = 'Form component';
            usage = 'Use for user input and data collection';
            tags = ['form', 'input', 'data'];
          } else if (contentStr.includes('image') || contentStr.includes('img')) {
            description = 'Image component';
            usage = 'Use for displaying images and graphics';
            tags = ['image', 'media', 'visual'];
          } else if (contentStr.includes('heading') || contentStr.includes('h1') || contentStr.includes('h2')) {
            description = 'Heading component';
            usage = 'Use for page titles and section headers';
            tags = ['heading', 'typography', 'title'];
          } else if (contentStr.includes('paragraph') || contentStr.includes('p')) {
            description = 'Text paragraph component';
            usage = 'Use for body text content';
            tags = ['text', 'paragraph', 'content'];
          } else {
            description = `${label || id} component`;
            usage = 'Use as needed for your layout';
            tags = ['general', 'component'];
          }
        }
      }
      
      components[category][id] = {
        name: label || id,
        description: description,
        usage: usage || `Use ${label || id} for your website layout`,
        content: content,
        category: category,
        // Enhanced metadata fields
        tags: tags,
        difficulty: difficulty,
        props: props,
        dependencies: dependencies,
        responsive: responsive,
        accessibility: accessibility,
        seo: seo,
        interactions: interactions,
        examples: examples,
        bestPractices: bestPractices,
        // Computed fields
        searchableText: [
          label || id,
          description,
          ...tags,
          ...examples,
          category
        ].join(' ').toLowerCase(),
        complexity: difficulty === 'beginner' ? 1 : difficulty === 'intermediate' ? 2 : 3
      };
    });
    
    return components;
  }

  // Extract component registry from actual block manager
  const componentRegistry = extractBlockManagerComponents();

  // Block categories for AI awareness
  const blockCategories = {
    layout: 'Layout Elements - Sections, containers, grids, columns',
    basic: 'Basic Elements - Divs, lists, buttons, navigation',
    typography: 'Typography - Headings, paragraphs, text elements',
    media: 'Media - Images, videos, audio',
    components: 'Components - Reusable UI components',
    react: 'React Components - Custom React-based components',
    realEstate: 'Real Estate - Property-specific components and features',
    uikit: 'UIkit Components - UIkit 3 framework components'
  };

  // Available tools and features
  const availableTools = {
    layoutTools: {
      layoutEnhancer: {
        name: 'Layout Enhancer',
        description: 'Precision spacing tool for components',
        features: ['Margin control', 'Padding control', 'Spacing presets', 'Visual guides']
      },
      gridSystem: {
        name: 'Grid System',
        description: 'Flexible grid layout system',
        features: ['Responsive columns', 'Auto-sizing', 'Gap control', 'Alignment options']
      }
    },
    contentTools: {
      gptPlugin: {
        name: 'AI Content Generator',
        description: 'GPT-powered content creation',
        features: ['Headlines', 'Paragraphs', 'Descriptions', 'Call-to-actions', 'Meta descriptions', 'Social media content', 'Email subject lines', 'Product descriptions', 'Real estate listings', 'Agent bios']
      },
      googleTranslate: {
        name: 'Google Translate',
        description: 'Multi-language translation',
        features: ['12+ languages', 'Auto-detection', 'Simple layout', 'Page translation']
      }
    },
    designTools: {
      deviceManager: {
        name: 'Device Manager',
        description: 'Responsive design testing',
        features: ['Desktop', 'Tablet', 'Mobile portrait', 'Mobile landscape']
      },
      styleManager: {
        name: 'Style Manager',
        description: 'CSS property editing',
        features: ['Typography', 'Layout', 'Background', 'Border', 'Effects', 'Extra']
      },
      layerManager: {
        name: 'Layer Manager',
        description: 'Component hierarchy management',
        features: ['Component tree', 'Visibility control', 'Selection', 'Drag and drop']
      }
    },
    realEstateTools: {
      propertySearch: {
        name: 'Property Search',
        description: 'Real estate property search functionality',
        features: ['Location search', 'Property type filter', 'Price range', 'Bedrooms/Bathrooms', 'API integration']
      },
      mapboxIntegration: {
        name: 'Mapbox Integration',
        description: 'Interactive maps for properties',
        features: ['Property markers', 'Interactive maps', 'Location search', 'Custom styling']
      }
    }
  };

  // Real estate specific context
  const realEstateContext = {
    propertyTypes: ['House', 'Condo', 'Townhouse', 'Apartment', 'Land', 'Commercial'],
    propertyFeatures: ['Bedrooms', 'Bathrooms', 'Square footage', 'Lot size', 'Year built', 'Garage', 'Pool', 'Fireplace'],
    searchCriteria: ['Location', 'Price range', 'Property type', 'Bedrooms', 'Bathrooms', 'Square footage', 'Year built'],
    listingFields: ['Address', 'Price', 'Description', 'Photos', 'Features', 'Agent info', 'Contact details'],
    commonPages: ['Homepage', 'Property search', 'Property details', 'Agent profiles', 'Contact', 'About', 'Blog']
  };

  // Generate comprehensive context for AI
  function generateAIContext() {
    const context = {
      availableComponents: componentRegistry,
      blockCategories: blockCategories,
      availableTools: availableTools,
      realEstateContext: realEstateContext,
      stylingFramework: 'UIkit 3',
      responsiveBreakpoints: ['Desktop', 'Tablet', 'Mobile'],
      cssClasses: {
        layout: ['uk-section', 'uk-container', 'uk-grid', 'uk-column-1-2'],
        basic: ['uk-padding-large', 'uk-list', 'uk-button', 'uk-navbar', 'uk-nav'],
        responsive: ['uk-width-1-1', 'uk-width-1-2', 'uk-width-1-3', 'uk-width-2-3'],
        utilities: ['uk-text-center', 'uk-text-left', 'uk-text-right', 'uk-margin', 'uk-padding']
      }
    };

    return context;
  }

  // Enhanced prompt generation with context
  function generateContextAwarePrompt(basePrompt, context = {}) {
    const aiContext = generateAIContext();
    
    const contextPrefix = `
You are an AI assistant for a GrapesJS website builder with the following capabilities:

AVAILABLE COMPONENTS (from actual block manager):
${Object.entries(aiContext.availableComponents).map(([category, components]) => 
  `${category.toUpperCase()}:\n${Object.entries(components).map(([key, comp]) => 
    `- ${comp.name}: ${comp.description} (Usage: ${comp.usage})`
  ).join('\n')}`
).join('\n\n')}

AVAILABLE TOOLS:
${Object.entries(aiContext.availableTools).map(([category, tools]) => 
  `${category.toUpperCase()}:\n${Object.entries(tools).map(([key, tool]) => 
    `- ${tool.name}: ${tool.description}`
  ).join('\n')}`
).join('\n\n')}

REAL ESTATE CONTEXT:
- Property Types: ${aiContext.realEstateContext.propertyTypes.join(', ')}
- Common Features: ${aiContext.realEstateContext.propertyFeatures.join(', ')}
- Search Criteria: ${aiContext.realEstateContext.searchCriteria.join(', ')}
- Common Pages: ${aiContext.realEstateContext.commonPages.join(', ')}

STYLING FRAMEWORK: UIkit 3 with responsive classes and utilities.

When suggesting components or layouts, always consider:
1. The specific context and purpose of the website
2. Available components and their appropriate usage
3. Real estate industry best practices
4. Responsive design principles
5. UIkit 3 classes and utilities

Base prompt: ${basePrompt}

Context: ${JSON.stringify(context)}
`;

    return contextPrefix;
  }

  // Component suggestion based on context
  function suggestComponents(context, requirements) {
    const suggestions = [];
    const availableComponents = generateAIContext().availableComponents;
    
    // Enhanced component analysis with metadata
    Object.values(availableComponents).forEach(category => {
      Object.entries(category).forEach(([id, component]) => {
        let relevanceScore = 0;
        let matchReasons = [];
        
        // Tag-based matching (high weight)
        if (component.tags) {
          const matchingTags = component.tags.filter(tag => 
            requirements.some(req => req.toLowerCase().includes(tag.toLowerCase()))
          );
          if (matchingTags.length > 0) {
            relevanceScore += matchingTags.length * 10;
            matchReasons.push(`Matches tags: ${matchingTags.join(', ')}`);
          }
        }
        
        // Example-based matching (medium weight)
        if (component.examples) {
          const matchingExamples = component.examples.filter(example =>
            requirements.some(req => req.toLowerCase().includes(example.toLowerCase()))
          );
          if (matchingExamples.length > 0) {
            relevanceScore += matchingExamples.length * 7;
            matchReasons.push(`Suitable for: ${matchingExamples.join(', ')}`);
          }
        }
        
        // Description-based matching (low weight)
        const descriptionMatches = requirements.filter(req =>
          component.description.toLowerCase().includes(req.toLowerCase())
        );
        if (descriptionMatches.length > 0) {
          relevanceScore += descriptionMatches.length * 3;
          matchReasons.push(`Description matches: ${descriptionMatches.join(', ')}`);
        }
        
        // Legacy fallback matching
        if (requirements.includes('hero') || requirements.includes('landing')) {
          if (component.description.toLowerCase().includes('hero') || 
              component.name.toLowerCase().includes('hero')) {
            relevanceScore += 15;
            matchReasons.push('Perfect for hero sections');
          }
        }
        
        if (requirements.includes('navigation') || requirements.includes('menu')) {
          if (component.tags?.includes('navigation') || 
              component.description.toLowerCase().includes('nav')) {
            relevanceScore += 15;
            matchReasons.push('Navigation component');
          }
        }
        
        if (requirements.includes('grid') || requirements.includes('layout')) {
          if (component.tags?.includes('grid') || 
              component.description.toLowerCase().includes('grid')) {
            relevanceScore += 12;
            matchReasons.push('Layout and grid component');
          }
        }
        
        if (requirements.includes('form') || requirements.includes('input')) {
          if (component.tags?.includes('form') || 
              component.description.toLowerCase().includes('form')) {
            relevanceScore += 15;
            matchReasons.push('Form and input component');
          }
        }
        
        // Add to suggestions if relevant
        if (relevanceScore > 0) {
          suggestions.push({
            component: id,
            name: component.name,
            description: component.description,
            usage: component.usage,
            relevanceScore: relevanceScore,
            matchReasons: matchReasons,
            difficulty: component.difficulty,
            tags: component.tags || [],
            examples: component.examples || [],
            props: component.props || {},
            dependencies: component.dependencies || [],
            responsive: component.responsive || {},
            accessibility: component.accessibility || {},
            seo: component.seo || {},
            bestPractices: component.bestPractices || []
          });
        }
      });
    });
    
    // Sort by relevance score (descending)
    return suggestions.sort((a, b) => b.relevanceScore - a.relevanceScore).slice(0, 10);
  }

  // New: Filter components by criteria
  function filterComponents(criteria = {}) {
    const { 
      difficulty, 
      tags, 
      dependencies, 
      category, 
      searchText,
      seoImportant = false,
      accessibilityImportant = false 
    } = criteria;
    
    const availableComponents = generateAIContext().availableComponents;
    const filtered = {};
    
    Object.entries(availableComponents).forEach(([categoryName, components]) => {
      // Category filter
      if (category && categoryName !== category) return;
      
      const filteredComponents = {};
      Object.entries(components).forEach(([id, component]) => {
        let passes = true;
        
        // Difficulty filter
        if (difficulty && component.difficulty !== difficulty) {
          passes = false;
        }
        
        // Tags filter (component must have at least one matching tag)
        if (tags && tags.length > 0) {
          const hasMatchingTag = tags.some(tag => 
            component.tags?.some(componentTag => 
              componentTag.toLowerCase().includes(tag.toLowerCase())
            )
          );
          if (!hasMatchingTag) passes = false;
        }
        
        // Dependencies filter
        if (dependencies && dependencies.length > 0) {
          const hasRequiredDependency = dependencies.every(dep =>
            component.dependencies?.includes(dep)
          );
          if (!hasRequiredDependency) passes = false;
        }
        
        // Search text filter
        if (searchText && component.searchableText) {
          if (!component.searchableText.includes(searchText.toLowerCase())) {
            passes = false;
          }
        }
        
        // SEO importance filter
        if (seoImportant && (!component.seo || component.seo.impact !== 'high')) {
          passes = false;
        }
        
        // Accessibility importance filter
        if (accessibilityImportant && !component.accessibility) {
          passes = false;
        }
        
        if (passes) {
          filteredComponents[id] = component;
        }
      });
      
      if (Object.keys(filteredComponents).length > 0) {
        filtered[categoryName] = filteredComponents;
      }
    });
    
    return filtered;
  }

  // New: Get component recommendations based on usage patterns
  function getRecommendations(context = {}) {
    const { 
      projectType = 'general',
      targetAudience = 'general',
      currentComponents = [],
      goals = []
    } = context;
    
    const recommendations = [];
    const availableComponents = generateAIContext().availableComponents;
    
    // Project type based recommendations
    const projectTypeRecommendations = {
      'real-estate': ['property-search', 'map', 'gallery', 'contact-form'],
      'e-commerce': ['product-grid', 'cart', 'checkout', 'reviews'],
      'blog': ['article', 'comments', 'sidebar', 'archive'],
      'portfolio': ['gallery', 'projects', 'testimonials', 'contact'],
      'corporate': ['services', 'team', 'testimonials', 'contact']
    };
    
    // Get base recommendations for project type
    const baseRecommendations = projectTypeRecommendations[projectType] || [];
    
    // Find components that match project type recommendations
    Object.values(availableComponents).forEach(category => {
      Object.entries(category).forEach(([id, component]) => {
        let score = 0;
        let reasons = [];
        
        // Check if component matches project type
        baseRecommendations.forEach(rec => {
          if (component.tags?.some(tag => tag.toLowerCase().includes(rec)) ||
              component.description.toLowerCase().includes(rec)) {
            score += 10;
            reasons.push(`Recommended for ${projectType} projects`);
          }
        });
        
        // Check if component addresses specific goals
        goals.forEach(goal => {
          if (component.examples?.some(example => 
            example.toLowerCase().includes(goal.toLowerCase())) ||
            component.tags?.some(tag => 
              tag.toLowerCase().includes(goal.toLowerCase()))) {
            score += 8;
            reasons.push(`Helps achieve: ${goal}`);
          }
        });
        
        // Bonus for high SEO impact components
        if (component.seo?.impact === 'high') {
          score += 5;
          reasons.push('High SEO impact');
        }
        
        // Bonus for accessibility-friendly components
        if (component.accessibility && Object.keys(component.accessibility).length > 0) {
          score += 3;
          reasons.push('Accessibility-friendly');
        }
        
        // Avoid already used components (but don't exclude completely)
        if (currentComponents.includes(id)) {
          score -= 5;
        }
        
        if (score > 0) {
          recommendations.push({
            componentId: id,
            component: component,
            score: score,
            reasons: reasons
          });
        }
      });
    });
    
    return recommendations
      .sort((a, b) => b.score - a.score)
      .slice(0, 8); // Top 8 recommendations
  }

  // New: Analyze component compatibility
  function analyzeComponentCompatibility(componentIds) {
    const availableComponents = generateAIContext().availableComponents;
    const analysis = {
      compatible: true,
      warnings: [],
      suggestions: [],
      dependencyConflicts: [],
      enhancementOpportunities: []
    };
    
    const components = [];
    const allDependencies = new Set();
    const allTags = new Set();
    
    // Gather components and their metadata
    componentIds.forEach(id => {
      Object.values(availableComponents).forEach(category => {
        if (category[id]) {
          components.push(category[id]);
          category[id].dependencies?.forEach(dep => allDependencies.add(dep));
          category[id].tags?.forEach(tag => allTags.add(tag));
        }
      });
    });
    
    // Check for dependency conflicts
    if (allDependencies.size > 1) {
      analysis.warnings.push('Multiple framework dependencies detected. Ensure compatibility.');
    }
    
    // Check for layout conflicts
    const layoutComponents = components.filter(comp => 
      comp.tags?.includes('layout') || comp.category === 'layout'
    );
    if (layoutComponents.length > 3) {
      analysis.warnings.push('Many layout components selected. Consider simplifying structure.');
    }
    
    // Check for missing essential components
    const hasNavigation = components.some(comp => comp.tags?.includes('navigation'));
    const hasContent = components.some(comp => comp.tags?.includes('content'));
    
    if (!hasNavigation) {
      analysis.suggestions.push('Consider adding a navigation component for better user experience.');
    }
    
    if (!hasContent) {
      analysis.suggestions.push('Add content components to provide value to users.');
    }
    
    // Enhancement opportunities
    const hasAccessibilityFeatures = components.some(comp => 
      comp.accessibility && Object.keys(comp.accessibility).length > 0
    );
    
    if (!hasAccessibilityFeatures) {
      analysis.enhancementOpportunities.push(
        'Consider accessibility-enhanced components for better inclusivity.'
      );
    }
    
    const hasHighSEOImpact = components.some(comp => comp.seo?.impact === 'high');
    if (!hasHighSEOImpact) {
      analysis.enhancementOpportunities.push(
        'Add components with high SEO impact for better search visibility.'
      );
    }
    
    return analysis;
  }

  // Enhanced content generation with component awareness
  function generateComponentAwareContent(prompt, context = {}) {
    const enhancedPrompt = generateContextAwarePrompt(prompt, context);
    
    // This would integrate with your existing GPT plugin
    if (window.gptPluginInstance) {
      return window.gptPluginInstance.generateContent(enhancedPrompt);
    }
    
    return enhancedPrompt;
  }

  // Register enhanced commands
  function registerEnhancedCommands() {
    editor.Commands.add('ai-suggest-components', {
      run: (editor, sender, options) => {
        const requirements = options.requirements || [];
        const context = options.context || {};
        const suggestions = suggestComponents(context, requirements);
        
        // Display suggestions in a modal or panel
        showComponentSuggestions(suggestions);
      }
    });

    editor.Commands.add('ai-generate-context-aware', {
      run: (editor, sender, options) => {
        const prompt = options.prompt || '';
        const context = options.context || {};
        const content = generateComponentAwareContent(prompt, context);
        
        // Apply content to selected component or create new component
        applyGeneratedContent(content, editor);
      }
    });

    // New enhanced commands
    editor.Commands.add('ai-filter-components', {
      run: (editor, sender, options) => {
        const criteria = options.criteria || {};
        const filtered = filterComponents(criteria);
        
        console.log('🔍 Filtered components:', filtered);
        return filtered;
      }
    });

    editor.Commands.add('ai-get-recommendations', {
      run: (editor, sender, options) => {
        const context = options.context || {};
        const recommendations = getRecommendations(context);
        
        console.log('💡 Component recommendations:', recommendations);
        return recommendations;
      }
    });

    editor.Commands.add('ai-analyze-compatibility', {
      run: (editor, sender, options) => {
        const componentIds = options.componentIds || [];
        const analysis = analyzeComponentCompatibility(componentIds);
        
        console.log('🔬 Compatibility analysis:', analysis);
        return analysis;
      }
    });

    editor.Commands.add('ai-enhanced-suggest', {
      run: (editor, sender, options) => {
        const requirements = options.requirements || [];
        const context = options.context || {};
        const suggestions = suggestComponents(context, requirements);
        
        console.log('🚀 Enhanced suggestions:', suggestions);
        return suggestions;
      }
    });
  }

  // Show component suggestions
  function showComponentSuggestions(suggestions) {
    const modal = document.createElement('div');
    modal.className = 'ai-suggestions-modal';
    modal.innerHTML = `
      <div class="ai-suggestions-content">
        <h3>AI Component Suggestions</h3>
        <div class="suggestions-list">
          ${suggestions.map(suggestion => `
            <div class="suggestion-item">
              <h4>${suggestion.component}</h4>
              <p><strong>Reason:</strong> ${suggestion.reason}</p>
              <p><strong>Usage:</strong> ${suggestion.usage}</p>
              <button onclick="addComponent('${suggestion.component}')">Add Component</button>
            </div>
          `).join('')}
        </div>
        <button onclick="closeSuggestions()">Close</button>
      </div>
    `;
    
    document.body.appendChild(modal);
  }

  // Apply generated content
  function applyGeneratedContent(content, editor) {
    const selected = editor.getSelected();
    
    if (selected) {
      // Apply to selected component
      selected.set('content', content);
    } else {
      // Create new component with content
      const component = editor.addComponent({
        type: 'text',
        content: content
      });
    }
  }

  // Initialize the enhancer
  function init() {
    console.log('🤖 Initializing AI Context Enhancer...');
    
    // Extract components with enhanced metadata
    const componentRegistry = extractBlockManagerComponents();
    console.log('📦 Available components from block manager:', Object.keys(componentRegistry));
    
    // Log metadata enhancement status
    let enhancedCount = 0;
    let totalCount = 0;
    Object.values(componentRegistry).forEach(category => {
      Object.values(category).forEach(component => {
        totalCount++;
        if (component.tags && component.tags.length > 0) {
          enhancedCount++;
        }
      });
    });
    
    console.log(`📊 Component metadata status: ${enhancedCount}/${totalCount} components have enhanced metadata`);
    
    // Register enhanced commands
    registerEnhancedCommands();
    
    // Make context available globally
    window.aiContext = generateAIContext();
    window.generateContextAwarePrompt = generateContextAwarePrompt;
    window.suggestComponents = suggestComponents;
    window.generateComponentAwareContent = generateComponentAwareContent;
    
    // New enhanced functions
    window.filterComponents = filterComponents;
    window.getComponentRecommendations = getRecommendations;
    window.analyzeComponentCompatibility = analyzeComponentCompatibility;
    window.extractBlockManagerComponents = extractBlockManagerComponents;
    
    console.log('✅ AI Context Enhancer initialized successfully');
    console.log('🔧 Available global functions:');
    console.log('  - generateContextAwarePrompt()');
    console.log('  - suggestComponents()');
    console.log('  - filterComponents()');
    console.log('  - getComponentRecommendations()');
    console.log('  - analyzeComponentCompatibility()');
    console.log('  - extractBlockManagerComponents()');
  }

  // Initialize
  init();

  // Return public API
  return {
    generateAIContext,
    generateContextAwarePrompt,
    suggestComponents,
    generateComponentAwareContent,
    componentRegistry,
    availableTools,
    realEstateContext,
    // Enhanced API
    extractBlockManagerComponents,
    filterComponents,
    getRecommendations,
    analyzeComponentCompatibility,
    // Utility functions
    version: '2.0.0',
    features: [
      'Enhanced metadata extraction',
      'Advanced component filtering',
      'Intelligent recommendations',
      'Compatibility analysis',
      'SEO and accessibility awareness',
      'Project-type specific suggestions'
    ]
  };
} 